import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Principal } from '../shared';
import { ParamContext } from '../shared/service/param.context';
import { LabelPrintingType } from '../label-printing/label-printing.component';
import { Subject } from 'rxjs';
import { take } from 'rxjs/operators';

@Injectable({ providedIn: 'root' })
export class StudyEditorService {

    private refreshSubject = new Subject<void>();
    private refreshCompletedSubject = new Subject<void>();

    constructor(private router: Router,
                private principal: Principal,
                private paramContext: ParamContext) { }

    get refresh$() {
        return this.refreshSubject.asObservable();
    }

    get refreshCompleted$() {
        return this.refreshCompletedSubject.asObservable();
    }

    /**
     * Triggers a refresh event by emitting to refreshSubject.
     * This will notify all subscribers of refresh$ observable that they should refresh their data.
     */
    triggerRefresh() {
        this.refreshSubject.next();
    }

    /**
     * Triggers a refresh and returns a Promise that resolves when the refresh is completed.
     * This is useful when you need to wait for a refresh operation to complete before continuing.
     *
     * The method works by:
     * 1. Listening for one emission from refreshCompleted$ observable
     * 2. Triggering a refresh by emitting on refreshSubject
     * 3. Resolving the promise when the refresh completes
     *
     * @returns Promise that resolves when the refresh operation completes
     */
    triggerRefreshAndWait(): Promise<void> {
        return new Promise((resolve) => {
            this.refreshCompleted$.pipe(take(1)).toPromise().then(() => {
                resolve();
            });
            this.refreshSubject.next();
        });
    }

    notifyRefreshCompleted() {
        this.refreshCompletedSubject.next();
    }

    navigateToTab(studyId: number, tabName: 'study-settings'
        | 'germplasm-and-checks'
        | 'treatment-factors'
        | 'environments'
        | 'experimental-design'
        | 'crosses-and-selections'
        | 'inventory'
        | 'sample-genotype') {

        this.router.navigate(['/', 'study-editor', studyId], {
            queryParamsHandling: 'merge', onSameUrlNavigation: 'reload' }).then(() => {
            this.router.navigate(['/', 'study-editor', studyId, { outlets:{ 'study-editor-outlet': tabName } }], { queryParamsHandling: 'merge', onSameUrlNavigation: 'reload' });
        });
    }

    /**
     * Navigates to the dataset tab with page reload.
     * Use this method when you need to reload the page to reflect changes.
     * For switching between datasets without reload, use navigateToDatasetTabNoReload()
     *
     * @param studyId unique identifier of the study
     * @param datasetId unique identifier of the dataset to navigate to
     */
    navigateToDatasetTab(studyId: number, datasetId: number) {
        this.router.navigate(['/', 'study-editor', studyId], {
            queryParamsHandling: 'merge', onSameUrlNavigation: 'reload'
        }).then(() => {
            this.router.navigate(['/', 'study-editor', studyId, { outlets: { 'study-editor-outlet': ['study-dataset', datasetId] } }], {
                queryParamsHandling: 'merge',
                onSameUrlNavigation: 'reload'
            });
        });
    }

    navigateToSampleListTab(studyId: number, listId: number) {
        this.router.navigate(['/', 'study-editor', studyId], {
            queryParamsHandling: 'merge', onSameUrlNavigation: 'reload'
        }).then(() => {
            this.router.navigate(['/', 'study-editor', studyId, { outlets: { 'study-editor-outlet': ['sample-list', listId] } }], {
                queryParamsHandling: 'merge',
                onSameUrlNavigation: 'reload'
            });
        });
    }

    /**
     * Navigate to dataset tab without reloading the page.
     * Useful when switching between datasets to avoid unnecessary page reloads.
     * @param studyId unique identifier of the study
     * @param datasetId unique identifier of the dataset to navigate to
     */
    navigateToDatasetTabNoReload(studyId: number, datasetId: number) {
        return this.router.navigate(['/', 'study-editor', studyId, { outlets: { 'study-editor-outlet': ['study-dataset', datasetId] } }], { queryParamsHandling: 'merge' });
    }

    navigateToSampleLabelPrintingView(studyId: number, datasetId: number, sampleListId: number, selectedColumns: string[], printingLabelType: LabelPrintingType) {
        this.router.navigate(['/', 'label-printing-main'], {
            queryParams: {
                cropName: this.paramContext.cropName,
                programUUID: this.paramContext.programUUID,
                studyId: studyId,
                datasetId: datasetId,
                sampleListId: sampleListId,
                selectedColumns: selectedColumns,
                printingLabelType: printingLabelType,
                openedDirectlyFromNewStudyEditor: true
            },
            queryParamsHandling: 'merge',
            onSameUrlNavigation: 'reload'
        }).then(() => {
        });
    }

    navigateToLabelPrintingView(studyId: number, datasetId: number, sampleListId: number, printingLabelType: LabelPrintingType) {
        this.router.navigate(['/', 'label-printing-main'], {
            queryParams: {
                cropName: this.paramContext.cropName,
                programUUID: this.paramContext.programUUID,
                studyId: studyId,
                datasetId: datasetId,
                sampleListId: sampleListId,
                printingLabelType: printingLabelType,
                openedDirectlyFromNewStudyEditor: true
            },
            queryParamsHandling: 'merge',
            onSameUrlNavigation: 'reload'
        }).then(() => {
        });
    }

    navigateToDesignCrosses(studyId: number, studyName: string)  {
        this.router.navigate(['/', 'design-crosses-main'], {
            queryParams: {
                studyId: studyId,
                studyName: studyName,
                openedDirectlyFromNewStudyEditor: true
            },
            queryParamsHandling: 'merge',
            onSameUrlNavigation: 'reload'
        }).then(() => {
        });
    }

    navigateToFieldPlan(studyId: number)  {
        this.router.navigate(['/', 'field-map-main'], {
            queryParams: {
                studyId: studyId,
                openedDirectlyFromNewStudyEditor: true
            },
            queryParamsHandling: 'merge',
            onSameUrlNavigation: 'reload'
        }).then(() => {
        });
    }

}
